import { WebPlanTier } from '../../constants/allowance';
import { TimeToSecond } from '../../constants/general';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { UserId, Uuid } from '../Base';
import { RepositoryElement } from '../Repository';
import { SubscriptionType } from '../SpaceStore';
import { Money, MoneyJson } from '../UserStore';

import { RewardPlaform } from './RewardCategory';
import { ReferralSystem, RewardReferralRole } from './RewardReferral';

export enum RewardRedemptionTabState {
  All = 'all',
  NotDelivered = 'notDelivered',
  Delivered = 'delivered',
  Completed = 'completed',
  Refund = 'refund',
  Pending = 'pending',
  Failed = 'failed',
  Paying = 'paying',
}

export enum RewardRedemptionState {
  Pending = 'pending',
  Paying = 'paying',
  Failed = 'failed',
  Redeemed = 'redeemed',
  Fulfilled = 'fulfilled',
  RefundRequested = 'refundRequested',
  RefundRejected = 'refundRejected',
  RefundCompleted = 'refundCompleted',
}

export interface Recipient {
  region: string;
  mailingAddress: string;
  phoneNumber: string;
}

export interface Delivery {
  deliveredBy: string;
  shippingCompany: string;
  shipmentTrackingNumber: string;
  shipmentTrackingWebsite?: string;
}

export enum RewardRedemptionType {
  Diamond = 'diamond',
  Token = 'token',
  Bread = 'bread',
  AvatarItem = 'avatarItem',
  SubscriptionKey = 'subscriptionKey',
  Coupon = 'coupon',
  GiftCard = 'giftCard',
  PhysicalGood = 'physicalGood',
  Certificate = 'certificate',
  ReferralRole = 'referralRole',
  MembershipTier = 'classroomMembershipTier',
  ClassroomMembershipTierSet = 'classroomMembershipTierSet',
  ClassroomPackage = 'classroomPackage',
  WebPlan = 'webPlan',
}

interface Diamond {
  type: RewardRedemptionType.Diamond;
  quantity: number;
}

interface Token {
  type: RewardRedemptionType.Token;
  quantity: number;
}

interface Bread {
  type: RewardRedemptionType.Bread;
  quantity: number;
}

interface AvatarItem {
  type: RewardRedemptionType.AvatarItem;
  itemId: string;
}

interface SubscriptionKey {
  type: RewardRedemptionType.SubscriptionKey;
  subscriptionKey: Uuid;
}

interface Coupon {
  type: RewardRedemptionType.Coupon;
  couponCode: string;
}

interface GiftCard {
  type: RewardRedemptionType.GiftCard;
  giftCardCode: string;
}

interface PhysicalGood {
  type: RewardRedemptionType.PhysicalGood;
}

type Cerificate = {
  type: RewardRedemptionType.Certificate;
  certificateNumber: string;
};

type ReferralRole = {
  type: RewardRedemptionType.ReferralRole;
  role: RewardReferralRole.Partner | RewardReferralRole.Distributor;
};

type MembershipTier = {
  type: RewardRedemptionType.MembershipTier;
  classroomId: Uuid;
  tierId: Uuid;
};

type ClassroomMembershipTierSet = {
  type: RewardRedemptionType.ClassroomMembershipTierSet;
  classroomId: Uuid;
  tierId: Uuid;
  subscriptionType:
    | SubscriptionType.Monthly
    | SubscriptionType.Yearly
    | SubscriptionType.Permanent;
};

type ClassroomPackageItem = {
  classroomId: Uuid;
  tierId: Uuid;
  subscriptionType:
    | SubscriptionType.Monthly
    | SubscriptionType.Yearly
    | SubscriptionType.Permanent;
};

type ClassroomPackage = {
  type: RewardRedemptionType.ClassroomPackage;
  creationToken?: number;
  hasAIRevision?: boolean;
  items?: ClassroomPackageItem[];
};

type WebPlan = {
  type: RewardRedemptionType.WebPlan;
  tier: WebPlanTier;
  period: SubscriptionType.Monthly | SubscriptionType.Yearly;
};

export type RewardRedemptionReward =
  | Diamond
  | Token
  | Bread
  | AvatarItem
  | SubscriptionKey
  | Coupon
  | GiftCard
  | PhysicalGood
  | Cerificate
  | ReferralRole
  | MembershipTier
  | ClassroomMembershipTierSet
  | ClassroomPackage
  | WebPlan;

type Payment = {
  gateway: 'stripe' | 'paypal';
  sessionId: string;
  url: string;
  currency: string;
  total: number;
  subscriptionId?: string;
};

enum ReferralState {
  Pending = 'pending',
  PartiallyCommitted = 'partiallyCommitted',
  Committed = 'committed',
}

type Referral = {
  referralSystem: ReferralSystem;
  state: ReferralState;
  partnerId: UserId | null;
  distributorId: UserId | null;
  referralReward: MoneyJson;
  partnerReward: MoneyJson;
  distributorReward: MoneyJson;
  tier1CustomerReward: MoneyJson;
  tier2CustomerReward: MoneyJson;
};

export interface RewardRedemptionJson<T = number> {
  rewardRedemptionId: Uuid;
  rewardOfferId: Uuid;
  store: RewardPlaform;
  userId: UserId;
  state: RewardRedemptionState;
  createdAt: T;
  processedAt?: T;
  fulfilledAt?: T;
  completedAt?: T;
  completionExtendedAt?: T;
  refundStartedAt?: T;
  lastRemindedAt?: T;
  quantity: number;
  variant?: Record<string, string>;
  recipient?: Recipient;
  reward?: RewardRedemptionReward;
  price?: MoneyJson;
  payment?: Payment;
  delivery?: Delivery;
  referral?: Referral;
}

export class RewardRedemption
  extends RepositoryElement
  implements RewardRedemptionJson<Date>
{
  readonly rewardRedemptionId!: Uuid;
  rewardOfferId!: Uuid;
  store!: RewardPlaform;
  userId!: UserId;
  state!: RewardRedemptionState;
  createdAt!: Date;
  processedAt?: Date;
  fulfilledAt?: Date;
  completedAt?: Date;
  completionExtendedAt?: Date;
  refundStartedAt?: Date;
  recipient?: Recipient;
  reward?: RewardRedemptionReward;
  delivery?: Delivery;
  lastRemindedAt?: Date;
  variant?: { [key: string]: string };
  price?: MoneyJson;
  payment?: Payment;
  quantity!: number;
  referral?: Referral;

  constructor(rewardRedemption: RewardRedemptionJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(rewardRedemption));
  }

  get isExtended(): boolean {
    return this.completionExtendedAt !== undefined;
  }

  id(): string {
    return this.rewardRedemptionId;
  }

  toJSON(): RewardRedemptionJson {
    return deepTransformDate(this) as unknown as RewardRedemptionJson;
  }

  isCompleted(now = Date.now()): boolean {
    return (this.completedAt?.getTime() ?? Infinity) <= now;
  }

  canExtend(now = Date.now()): boolean {
    const diff = (this.completedAt?.getTime() ?? Infinity) - now;
    return !this.isExtended && diff <= 2 * TimeToSecond.Day * 1000 && diff > 0;
  }

  canShow(now = Date.now()): boolean {
    // convert the calculated difference to a Date
    const diff = new Date(
      now -
        Math.max(
          this.createdAt.getTime(),
          this.processedAt?.getTime() ?? 0,
          this.fulfilledAt?.getTime() ?? 0,
          this.completedAt?.getTime() ?? 0,
          this.refundStartedAt?.getTime() ?? 0,
        ),
    );
    // show anything within 2 years (leap year considered)
    return Math.abs(diff.getUTCFullYear() - 1970) < 2;
  }

  getTabState(
    now = Date.now(),
  ): Exclude<RewardRedemptionTabState, RewardRedemptionTabState.All> {
    switch (this.state) {
      case RewardRedemptionState.Pending:
      case RewardRedemptionState.Paying:
        return RewardRedemptionTabState.Pending;
      case RewardRedemptionState.Failed:
        return RewardRedemptionTabState.Failed;
      case RewardRedemptionState.Redeemed:
        return RewardRedemptionTabState.NotDelivered;
      case RewardRedemptionState.Fulfilled:
        return this.isCompleted(now)
          ? RewardRedemptionTabState.Completed
          : RewardRedemptionTabState.Delivered;
      case RewardRedemptionState.RefundRequested:
        return RewardRedemptionTabState.Refund;
      case RewardRedemptionState.RefundRejected:
        return RewardRedemptionTabState.Refund;
      case RewardRedemptionState.RefundCompleted:
        return RewardRedemptionTabState.Refund;
    }
  }

  showRemindDelivery(now = Date.now()): boolean {
    return now - this.createdAt.getTime() >= 1 * TimeToSecond.Day * 1000; // after one day from redemption
  }

  canRemindDelivery(now = Date.now()): boolean {
    const tabState = this.getTabState(now);
    return (
      tabState === RewardRedemptionTabState.NotDelivered &&
      this.showRemindDelivery(now) &&
      now - (this.lastRemindedAt?.getTime() ?? 0) >= 1 * TimeToSecond.Day * 1000 // after one day from last reminder
    );
  }

  isLatestVersion(redemption: RewardRedemption): boolean {
    const aState = this.state;
    const bState = redemption.state;
    const stateOrder: Record<RewardRedemptionState, number> = {
      [RewardRedemptionState.Pending]: 0,
      [RewardRedemptionState.Paying]: 0,
      [RewardRedemptionState.Redeemed]: 0.5,
      [RewardRedemptionState.Failed]: 1,
      [RewardRedemptionState.Fulfilled]: 1,
      [RewardRedemptionState.RefundRequested]: 2,
      [RewardRedemptionState.RefundCompleted]: 2,
      [RewardRedemptionState.RefundRejected]: 2,
    };
    return (
      (this.isCompleted() && !redemption.isCompleted()) ||
      stateOrder[aState] > stateOrder[bState]
    );
  }

  static tabStateToState(tabState: RewardRedemptionTabState): {
    state: RewardRedemptionState[];
    isCompleted?: boolean;
  } {
    switch (tabState) {
      case RewardRedemptionTabState.All:
        return {
          state: [
            RewardRedemptionState.Failed,
            RewardRedemptionState.Fulfilled,
            RewardRedemptionState.Pending,
            RewardRedemptionState.Redeemed,
            RewardRedemptionState.RefundCompleted,
            RewardRedemptionState.RefundRejected,
            RewardRedemptionState.RefundRequested,
          ],
        };
      case RewardRedemptionTabState.NotDelivered:
        return {
          state: [RewardRedemptionState.Redeemed],
        };
      case RewardRedemptionTabState.Delivered:
        return {
          state: [RewardRedemptionState.Fulfilled],
          isCompleted: false,
        };
      case RewardRedemptionTabState.Completed:
        return {
          state: [RewardRedemptionState.Fulfilled],
          isCompleted: true,
        };
      case RewardRedemptionTabState.Refund:
        return {
          state: [
            RewardRedemptionState.RefundCompleted,
            RewardRedemptionState.RefundRejected,
            RewardRedemptionState.RefundRequested,
          ],
        };
      case RewardRedemptionTabState.Failed:
        return {
          state: [RewardRedemptionState.Failed],
        };
      case RewardRedemptionTabState.Pending:
        return {
          state: [RewardRedemptionState.Pending],
        };
      case RewardRedemptionTabState.Paying:
        return {
          state: [RewardRedemptionState.Paying],
        };
    }
  }

  getConfirmReward(role: RewardReferralRole, isTier2?: boolean) {
    let money;
    if (role === RewardReferralRole.Distributor) {
      money = this.referral?.distributorReward;
    } else if (role === RewardReferralRole.Partner) {
      money = this.referral?.partnerReward;
    } else if (role === RewardReferralRole.Customer) {
      if (isTier2) {
        money = this.referral?.tier2CustomerReward;
      }
      money = this.referral?.tier1CustomerReward;
    }
    if (money) {
      return new Money(money);
    }
    return undefined;
  }
}
